import { __assign, __rest } from "tslib";
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
// Form/Form* components are exceptions for react-hook-form
// eslint-disable-next-line no-restricted-imports
import { useForm, FormProvider } from 'react-hook-form';
import FormCheckbox from './Form.Checkbox';
import FormDateInput from './Form.DateInput';
import FormEmailRecipientsInput from './Form.EmailRecipientsInput';
import FormTextArea from './Form.TextArea';
import FormFieldDependentContent from './Form.FieldDependentContent';
import FormTextInput from './Form.TextInput';
import FormRadioButtonGroup from './Form.RadioButtonGroup';
import FormSelectMenu from './Form.SelectMenu';
import FormSubmitButton from './Form.SubmitButton';
import FormAdvancedSelect from './Form.AdvancedSelect';
import FormAutocomplete from './Form.Autocomplete';
import FormRating from './Form.Rating';
import FormRichTextField from './Form.RichTextField';
import FormSelect from './Form.Select';
import { useTemporaryBrowserData } from './TemporaryBrowserData';
var Form = function (_a) {
    var children = _a.children, className = _a.className, initialValues = _a.initialValues, onSubmit = _a.onSubmit, props = __rest(_a, ["children", "className", "initialValues", "onSubmit"]);
    var _b = useForm({
        defaultValues: initialValues,
        // Form validation is triggered within the input components using formState.isValid.
        // To use formState.isValid the mode need to be `onBlur`, `onTouched` or `onChange`.
        mode: 'onBlur'
    }), createSubmitHandler = _b.handleSubmit, formProviderProps = __rest(_b, ["handleSubmit"]);
    var deleteData = useTemporaryBrowserData().deleteData;
    // react-hook-form's method handleSubmit is aliased as createSubmitHandler.
    // It returns a handler function which is the combination of its internal handler plus our custom callback.
    var handleFormSubmit = createSubmitHandler(deleteData
        ? function (formData) { return onSubmit(formData, deleteData); }
        : onSubmit);
    var formClassName = classnames('Form', className);
    /**
     * Based on code snippets in react-hook-form's docs, we should be spreading everything
     * from useForm into the FormProvider AND we can/should(?) pass our own callback to onSubmit
     * if we want to inject our own logic into the form submission process
     *
     * https://react-hook-form.com/advanced-usage/#FormProviderPerformance (reference for code example, not content/topic of section)
     */
    return (React.createElement(FormProvider, __assign({}, formProviderProps, { handleSubmit: createSubmitHandler }),
        React.createElement("form", __assign({}, props, { className: formClassName, onSubmit: handleFormSubmit }), children)));
};
Form.AdvancedSelect = FormAdvancedSelect;
Form.Autocomplete = FormAutocomplete;
Form.Checkbox = FormCheckbox;
Form.DateInput = FormDateInput;
Form.EmailRecipientsInput = FormEmailRecipientsInput;
Form.FieldDependentContent = FormFieldDependentContent;
Form.RadioButtonGroup = FormRadioButtonGroup;
Form.SubmitButton = FormSubmitButton;
Form.SelectMenu = FormSelectMenu;
Form.TextArea = FormTextArea;
Form.TextInput = FormTextInput;
Form.Rating = FormRating;
Form.RichTextField = FormRichTextField;
Form.Select = FormSelect;
Form.defaultProps = {
    initialValues: {}
};
Form.propTypes = {
    /** The content to render */
    children: PropTypes.node.isRequired,
    /** Class name to apply to the container div */
    className: PropTypes.string,
    /** DEPRECATED: use initialValue prop on input instead  */
    initialValues: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.array,
    ])),
    /** Callback function that is called when form is submitted. */
    onSubmit: PropTypes.func.isRequired
};
export default Form;
//# sourceMappingURL=Form.js.map